import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Grid,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import api, { handleApiError } from '../utils/api';

const KvkkManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [configurations, setConfigurations] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    title: '',
    contentType: 'html',
    content: '',
    pdfUrl: '',
    required: true,
    displayOrder: 0,
    context: 'both',
    legalBasis: 'consent',
    retentionPeriodDays: 2555
  });

  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);

  useEffect(() => {
    loadConfigurations();
    loadStatistics();
  }, []);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/kvkk/admin/configurations');
      if (response.data.success) {
        setConfigurations(response.data.data);
      }
    } catch (error) {
      console.error('Error loading KVKK configurations:', error);
      setError('KVKK konfigürasyonları yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.get('/kvkk/admin/statistics');
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error loading KVKK statistics:', error);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Başlık gereklidir.';
    } else if (formData.title.length > 200) {
      errors.title = 'Başlık 200 karakteri geçemez.';
    }

    if (formData.contentType === 'html' && !formData.content.trim()) {
      errors.content = 'HTML içerik gereklidir.';
    }

    if (formData.contentType === 'pdf' && !formData.pdfUrl) {
      errors.pdfUrl = 'PDF dosyası gereklidir.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError('');

    try {
      const url = selectedConfig 
        ? `/kvkk/admin/configurations/${selectedConfig._id}`
        : '/kvkk/admin/configurations';
      
      const method = selectedConfig ? 'put' : 'post';
      
      const response = await api[method](url, formData);
      
      if (response.data.success) {
        setSuccess(`KVKK konfigürasyonu başarıyla ${selectedConfig ? 'güncellendi' : 'oluşturuldu'}.`);
        await loadConfigurations();
        await loadStatistics();
        handleCloseDialog();
      }
    } catch (error) {
      console.error('Error saving KVKK configuration:', error);
      setError(handleApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedConfig) return;

    setSaving(true);
    setError('');

    try {
      const response = await api.delete(`/kvkk/admin/configurations/${selectedConfig._id}`);
      
      if (response.data.success) {
        setSuccess(`KVKK konfigürasyonu başarıyla ${response.data.data.deleted ? 'silindi' : 'devre dışı bırakıldı'}.`);
        await loadConfigurations();
        await loadStatistics();
        setDeleteDialog(false);
        setSelectedConfig(null);
      }
    } catch (error) {
      console.error('Error deleting KVKK configuration:', error);
      setError(handleApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handlePdfUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Sadece PDF dosyaları yüklenebilir.');
      return;
    }

    setUploadingPdf(true);
    setError('');

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('kvkkPdf', file);

      const response = await api.post('/kvkk/admin/upload-pdf', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setFormData(prev => ({ ...prev, pdfUrl: response.data.data.pdfUrl }));
        setSelectedPdfFile(file.name);
        setSuccess('PDF başarıyla yüklendi.');
      }
    } catch (error) {
      console.error('Error uploading PDF:', error);
      setError(handleApiError(error));
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleEdit = (config) => {
    setSelectedConfig(config);
    setFormData({
      title: config.title,
      contentType: config.contentType || 'html',
      content: config.content || '',
      pdfUrl: config.pdfUrl || '',
      required: config.required,
      displayOrder: config.displayOrder,
      context: config.context,
      legalBasis: config.legalBasis,
      retentionPeriodDays: config.retentionPeriodDays
    });
    setSelectedPdfFile(config.pdfUrl ? config.pdfUrl.split('/').pop() : null);
    setFormErrors({});
    setEditDialog(true);
  };

  const handleCreate = () => {
    setSelectedConfig(null);
    setFormData({
      title: '',
      contentType: 'html',
      content: '',
      pdfUrl: '',
      required: true,
      displayOrder: configurations.length,
      context: 'both',
      legalBasis: 'consent',
      retentionPeriodDays: 2555
    });
    setSelectedPdfFile(null);
    setFormErrors({});
    setEditDialog(true);
  };

  const handlePreview = (config) => {
    setSelectedConfig(config);
    setPreviewDialog(true);
  };

  const handleCloseDialog = () => {
    setEditDialog(false);
    setDeleteDialog(false);
    setPreviewDialog(false);
    setSelectedConfig(null);
    setFormErrors({});
  };

  const initializeDefaults = async () => {
    try {
      setSaving(true);
      const response = await api.post('/kvkk/admin/initialize-defaults');
      
      if (response.data.success) {
        setSuccess(response.data.message);
        await loadConfigurations();
        await loadStatistics();
      }
    } catch (error) {
      console.error('Error initializing defaults:', error);
      setError(handleApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const sanitizePreview = (html) => {
    return {
      __html: DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
        ALLOWED_ATTR: ['class']
      })
    };
  };

  const getContextLabel = (context) => {
    switch (context) {
      case 'registration': return 'Üyelik';
      case 'profile_update': return 'Profil Güncelleme';
      case 'both': return 'Her İkisi';
      default: return context;
    }
  };

  const getLegalBasisLabel = (basis) => {
    switch (basis) {
      case 'consent': return 'Rıza';
      case 'contract': return 'Sözleşme';
      case 'legal_obligation': return 'Hukuki Yükümlülük';
      case 'vital_interests': return 'Yaşamsal Çıkarlar';
      case 'public_task': return 'Kamu Görevi';
      case 'legitimate_interests': return 'Meşru Menfaat';
      default: return basis;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          KVKK yönetimi yükleniyor...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            KVKK Yönetimi
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Kişisel veri işleme onaylarını ve içeriklerini yönetin.
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={initializeDefaults}
            disabled={saving}
            startIcon={<InfoIcon />}
          >
            Varsayılan Onayları Oluştur
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            startIcon={<AddIcon />}
          >
            Yeni Onay Ekle
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Statistics */}
      {statistics && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AnalyticsIcon color="primary" />
            İstatistikler
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {statistics.statistics.totalApprovals}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Toplam Onay
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="secondary">
                  {statistics.statistics.activeConfigurations}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Aktif Konfigürasyon
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="info.main">
                  {statistics.statistics.registrationApprovals}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Üyelik Onayı
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="warning.main">
                  {statistics.statistics.withdrawnApprovals}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Geri Çekilmiş
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Configurations List */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          KVKK Onay Konfigürasyonları ({configurations.length})
        </Typography>
        
        {configurations.length === 0 ? (
          <Box textAlign="center" py={4}>
            <SecurityIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Henüz KVKK onay konfigürasyonu bulunmuyor.
            </Typography>
            <Button
              variant="contained"
              onClick={handleCreate}
              startIcon={<AddIcon />}
            >
              İlk Konfigürasyonu Oluştur
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {configurations.map((config, index) => (
              <Grid item xs={12} md={6} key={config._id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom sx={{ wordBreak: 'break-word' }}>
                          {config.title}
                        </Typography>
                      </Box>
                      <Chip 
                        label={config.displayOrder}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip
                        label={config.required ? 'Zorunlu' : 'İsteğe Bağlı'}
                        color={config.required ? 'error' : 'default'}
                        size="small"
                      />
                      <Chip
                        label={getContextLabel(config.context)}
                        color="primary"
                        size="small"
                      />
                      <Chip
                        label={config.isActive ? 'Aktif' : 'Pasif'}
                        color={config.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    
                    <Typography variant="caption" color="text.secondary" display="block">
                      Versiyon: {config.version} | {getLegalBasisLabel(config.legalBasis)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Oluşturan: {config.createdBy?.firstName} {config.createdBy?.lastName}
                    </Typography>
                  </CardContent>
                  
                  <CardActions>
                    <Tooltip title="Önizle">
                      <IconButton onClick={() => handlePreview(config)} size="small">
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Düzenle">
                      <IconButton onClick={() => handleEdit(config)} size="small">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sil">
                      <IconButton 
                        onClick={() => {
                          setSelectedConfig(config);
                          setDeleteDialog(true);
                        }} 
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog 
        open={editDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {selectedConfig ? 'KVKK Onayını Düzenle' : 'Yeni KVKK Onayı'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Başlık"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                fullWidth
                required
                error={!!formErrors.title}
                helperText={formErrors.title}
                placeholder="Örn: Kişisel Veri İşleme Rızası"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>İçerik Tipi</InputLabel>
                <Select
                  value={formData.contentType}
                  onChange={(e) => setFormData({ ...formData, contentType: e.target.value })}
                  label="İçerik Tipi"
                >
                  <MenuItem value="html">HTML İçerik</MenuItem>
                  <MenuItem value="pdf">PDF Dosyası</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.contentType === 'html' ? (
              <Grid item xs={12}>
                <TextField
                  label="HTML İçerik"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  fullWidth
                  multiline
                  rows={8}
                  required
                  error={!!formErrors.content}
                  helperText={formErrors.content || 'HTML etiketleri kullanabilirsiniz (p, strong, ul, li, vb.)'}
                  placeholder="<p>KVKK uyarınca...</p>"
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Box>
                  <input
                    accept="application/pdf"
                    style={{ display: 'none' }}
                    id="pdf-upload"
                    type="file"
                    onChange={handlePdfUpload}
                    disabled={uploadingPdf}
                  />
                  <label htmlFor="pdf-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      disabled={uploadingPdf}
                      startIcon={uploadingPdf ? <CircularProgress size={20} /> : null}
                    >
                      {uploadingPdf ? 'PDF Yükleniyor...' : 'PDF Seç ve Yükle'}
                    </Button>
                  </label>
                  {selectedPdfFile && (
                    <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                      Seçili PDF: {selectedPdfFile}
                    </Typography>
                  )}
                  {formErrors.pdfUrl && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {formErrors.pdfUrl}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Bağlam</InputLabel>
                <Select
                  value={formData.context}
                  onChange={(e) => setFormData({ ...formData, context: e.target.value })}
                  label="Bağlam"
                >
                  <MenuItem value="registration">Üyelik</MenuItem>
                  <MenuItem value="profile_update">Profil Güncelleme</MenuItem>
                  <MenuItem value="both">Her İkisi</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Yasal Dayanak</InputLabel>
                <Select
                  value={formData.legalBasis}
                  onChange={(e) => setFormData({ ...formData, legalBasis: e.target.value })}
                  label="Yasal Dayanak"
                >
                  <MenuItem value="consent">Rıza</MenuItem>
                  <MenuItem value="contract">Sözleşme</MenuItem>
                  <MenuItem value="legal_obligation">Hukuki Yükümlülük</MenuItem>
                  <MenuItem value="vital_interests">Yaşamsal Çıkarlar</MenuItem>
                  <MenuItem value="public_task">Kamu Görevi</MenuItem>
                  <MenuItem value="legitimate_interests">Meşru Menfaat</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Görüntüleme Sırası"
                type="number"
                value={formData.displayOrder}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                fullWidth
                helperText="Düşük sayılar önce gösterilir"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Saklama Süresi (Gün)"
                type="number"
                value={formData.retentionPeriodDays}
                onChange={(e) => setFormData({ ...formData, retentionPeriodDays: parseInt(e.target.value) || 1 })}
                fullWidth
                helperText="Varsayılan: 2555 gün (~7 yıl)"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.required}
                    onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                  />
                }
                label="Bu onay zorunlu"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            İptal
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Önizleme: {selectedConfig?.title}
        </DialogTitle>
        <DialogContent dividers>
          {selectedConfig && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={selectedConfig.required ? 'Zorunlu' : 'İsteğe Bağlı'}
                  color={selectedConfig.required ? 'error' : 'default'}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Chip
                  label={getContextLabel(selectedConfig.context)}
                  color="primary"
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Chip
                  label={selectedConfig.contentType === 'pdf' ? 'PDF' : 'HTML'}
                  color="secondary"
                  size="small"
                />
              </Box>

              {selectedConfig.contentType === 'pdf' ? (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    height: 500,
                    overflow: 'hidden'
                  }}
                >
                  <iframe
                    src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${selectedConfig.pdfUrl}#toolbar=0`}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none'
                    }}
                    title={selectedConfig.title}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    '& p': { mb: 1 },
                    '& ul, & ol': { pl: 2, mb: 1 },
                    '& li': { mb: 0.5 },
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%'
                  }}
                  dangerouslySetInnerHTML={sanitizePreview(selectedConfig.content)}
                />
              )}

              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>İçerik Tipi:</strong> {selectedConfig.contentType === 'pdf' ? 'PDF Dosyası' : 'HTML İçerik'}<br />
                  <strong>Yasal Dayanak:</strong> {getLegalBasisLabel(selectedConfig.legalBasis)}<br />
                  <strong>Saklama Süresi:</strong> {selectedConfig.retentionPeriodDays} gün<br />
                  <strong>Versiyon:</strong> {selectedConfig.version}<br />
                  <strong>Oluşturulma:</strong> {new Date(selectedConfig.createdAt).toLocaleDateString('tr-TR')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={handleCloseDialog}>
        <DialogTitle>KVKK Onayını Sil</DialogTitle>
        <DialogContent>
          <Typography>
            "{selectedConfig?.title}" adlı KVKK onayını silmek istediğinizden emin misiniz?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Bu onaya ait kullanıcı onayları varsa, konfigürasyon tamamen silinmeyecek, 
            sadece devre dışı bırakılacaktır.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            İptal
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {saving ? 'Siliniyor...' : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KvkkManagement;